import { useEffect, useState, type ReactNode } from "react";
import { InlineHelp } from "@/components/common/InlineHelp";
import { getManufacturingMaterialUseDetail, listManufacturingMaterialUses } from "@/lib/manufacturing/materialUsesClient";
import {
  deleteInventoryMatchRule,
  listInventoryMatchRules,
  upsertInventoryMatchRule,
  type InventoryMatchIndicator,
  type InventoryMatchRule,
} from "@/lib/manufacturing/inventoryMatchClient";

const FIELDS: Array<{ id: InventoryMatchIndicator["field"]; label: string }> = [
  { id: "width", label: "Width" },
  { id: "length", label: "Length" },
  { id: "series", label: "Series" },
  { id: "roof_style", label: "Roof style" },
  { id: "package", label: "Package" },
  { id: "roof_type", label: "Roof type" },
  { id: "roof_color", label: "Roof color" },
  { id: "siding_type", label: "Siding type" },
  { id: "siding_color", label: "Siding color" },
  { id: "trim_color", label: "Trim color" },
  { id: "addon_name", label: "Add-on name" },
];

const inputClass =
  "rounded-md border border-gray-300 bg-white px-2 py-1 text-sm dark:border-zinc-600 dark:bg-zinc-900";

function blankIndicator(): InventoryMatchIndicator {
  return { field: "addon_name", op: "contains", value: "" };
}

function HelpClick({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex" onClick={(event) => event.stopPropagation()} onMouseDown={(event) => event.stopPropagation()}>
      {children}
    </span>
  );
}

export function InventoryOutlookIndicatorMap({ enabled }: { enabled: boolean }) {
  const [uses, setUses] = useState<Array<{ id: string; use_code: string; name: string }>>([]);
  const [members, setMembers] = useState<Array<{ material_id: string; material_name: string; material_code: string }>>([]);
  const [rules, setRules] = useState<InventoryMatchRule[]>([]);
  const [useId, setUseId] = useState("");
  const [materialId, setMaterialId] = useState("");
  const [priority, setPriority] = useState("100");
  const [indicators, setIndicators] = useState<InventoryMatchIndicator[]>([blankIndicator()]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    void Promise.all([listManufacturingMaterialUses({ active_only: true }), listInventoryMatchRules()]).then(([useRes, ruleRes]) => {
      if (!useRes.ok) return setError(useRes.error);
      if (!ruleRes.ok) return setError(ruleRes.error);
      setUses(useRes.data.uses.map((use) => ({ id: use.id, use_code: use.use_code, name: use.name })));
      setRules(ruleRes.data.rules);
    });
  }, [enabled]);

  useEffect(() => {
    if (!useId) {
      setMembers([]);
      return;
    }
    void getManufacturingMaterialUseDetail(useId, { active_only: true }).then((res) => {
      if (!res.ok) return setError(res.error);
      setMembers(
        res.data.members.map((member) => ({
          material_id: member.material_id,
          material_name: member.material_name,
          material_code: member.material_code,
        })),
      );
    });
  }, [useId]);

  async function saveRule() {
    setBusy(true);
    setError(null);
    const saved = await upsertInventoryMatchRule({
      material_use_id: useId,
      material_id: materialId,
      priority: Number(priority),
      indicators: indicators.filter((row) => row.value.trim()),
    });
    setBusy(false);
    if (!saved.ok) return setError(saved.error);
    setIndicators([blankIndicator()]);
    const listed = await listInventoryMatchRules();
    if (listed.ok) setRules(listed.data.rules);
  }

  async function removeRule(id: string) {
    setError(null);
    const out = await deleteInventoryMatchRule(id);
    if (!out.ok) return setError(out.error);
    setRules((prev) => prev.filter((rule) => rule.id !== id));
  }

  return (
    <div className="space-y-3">
      {error ? <p className="text-sm text-red-700 dark:text-red-300">{error}</p> : null}
      <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700 dark:text-zinc-300">
        Hand-written rules for a part use
        <HelpClick>
          <InlineHelp glyph="!" label="What these rules do">
            This chooses which stocked SKU to count when a part has more than one. It is not an on-hand alert. Example: the part use is the 70 inch panel and the order roof color is Black, so the outlook counts the black panel.
          </InlineHelp>
        </HelpClick>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700 dark:text-zinc-300">
        Which SKU fills the part
        <HelpClick>
          <InlineHelp glyph="!" label="Part use, SKU, and priority">
            Part use is the job on the building. Inventory SKU is the catalog item that fills that job when the rule matches. Priority is the tie-break: the lowest number wins. Two rules with the same number leave the part unresolved.
          </InlineHelp>
        </HelpClick>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select className={inputClass} value={useId} onChange={(e) => setUseId(e.target.value)}>
          <option value="">Part use</option>
          {uses.map((use) => (
            <option key={use.id} value={use.id}>
              {use.use_code} — {use.name}
            </option>
          ))}
        </select>
        <select className={inputClass} value={materialId} onChange={(e) => setMaterialId(e.target.value)}>
          <option value="">Inventory SKU</option>
          {members.map((member) => (
            <option key={member.material_id} value={member.material_id}>
              {member.material_code} — {member.material_name}
            </option>
          ))}
        </select>
        <input
          className={`${inputClass} w-24`}
          inputMode="numeric"
          value={priority}
          aria-label="Priority"
          placeholder="Priority"
          onChange={(e) => setPriority(e.target.value)}
        />
      </div>
      <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700 dark:text-zinc-300">
        When the order matches
        <InlineHelp glyph="!" label="What the indicator rows mean">
          Each row is a fact on the order that must be true before that SKU is chosen. Width equals 10. Roof color equals Black. Add-on name contains Loft. These are not quantities, and they are not an on-hand threshold.
        </InlineHelp>
      </div>
      {indicators.map((row, index) => (
        <div key={index} className="flex flex-wrap gap-2">
          <select
            className={inputClass}
            value={row.field}
            onChange={(e) => {
              const field = e.target.value;
              setIndicators((prev) =>
                prev.map((item, i) =>
                  i === index ? { ...item, field, op: field === "addon_name" ? item.op : "equals" } : item,
                ),
              );
            }}
          >
            {FIELDS.map((field) => (
              <option key={field.id} value={field.id}>
                {field.label}
              </option>
            ))}
          </select>
          <select
            className={inputClass}
            value={row.op}
            onChange={(e) =>
              setIndicators((prev) =>
                prev.map((item, i) =>
                  i === index ? { ...item, op: e.target.value === "contains" ? "contains" : "equals" } : item,
                ),
              )
            }
          >
            <option value="equals">equals</option>
            {row.field === "addon_name" ? <option value="contains">contains</option> : null}
          </select>
          <input
            className={inputClass}
            value={row.value}
            placeholder="Value"
            onChange={(e) =>
              setIndicators((prev) => prev.map((item, i) => (i === index ? { ...item, value: e.target.value } : item)))
            }
          />
        </div>
      ))}
      <div className="flex gap-2">
        <button type="button" className="text-sm underline" onClick={() => setIndicators((prev) => [...prev, blankIndicator()])}>
          Add indicator
        </button>
        <button
          type="button"
          disabled={busy || !useId || !materialId}
          className="rounded-md bg-gray-900 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50 dark:bg-zinc-100 dark:text-zinc-900"
          onClick={() => void saveRule()}
        >
          Save rule
        </button>
      </div>
      <ul className="space-y-2 text-sm">
        {rules.map((rule) => (
          <li key={rule.id} className="flex items-start justify-between gap-3 rounded-xl border border-gray-200 px-3 py-2 dark:border-zinc-700">
            <span>
              <span className="font-semibold">{rule.use_code}</span>
              {" → "}
              {rule.material_code || rule.material_name || rule.material_id}
              {" · priority "}
              {rule.priority}
              {" · "}
              {rule.indicators.map((item) => `${item.field} ${item.op} ${item.value}`).join("; ")}
            </span>
            <button type="button" className="text-xs underline" onClick={() => void removeRule(rule.id)}>
              Delete
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
